import React, {useCallback, useMemo, useEffect, useState, useRef} from "react"
import {useTranslation} from "react-i18next"
import "../i18n"
import {makeStyles} from "@material-ui/core/styles"
import {
  TableContainer,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableHead,
  Typography,
  useMediaQuery,
} from "@material-ui/core"
import {useStore, useDispatch} from "../Store"
import {useTheme, useWindowSize} from "../hooks"
import {useCookies} from "react-cookie"
import {nl2br, getTokenCookie} from "../common"
import {getSenarioRankList} from "../api"
import {loadBaseDate} from "../lib/LoadValues"
import SenarioTerm from "./SenarioTerm"
import SenarioTrend from "./SenarioTrend"
import SenarioCategory from "./SenarioCategory"
import {pink, green} from "@material-ui/core/colors"
import TrendingUpIcon from "@material-ui/icons/TrendingUp"
import TrendingDownIcon from "@material-ui/icons/TrendingDown"

const useStyles = makeStyles((theme) => ({
  root: {
    display: `flex`,
    flexDirection: `column`,
    textAlign: `center`,
    marginBottom: `24px`,
    zIndex: (props) => (props.zIndex === props.className ? 1201: 0),
  },
  grid: {
    display: (props) => (props.isMobile ? `block` : `grid`),
    gridTemplateColumns: (props) => (props.isMobile ? `350px 1fr` : `275px 1fr`),
    margin: `0px 24px`,
  },
  content: {
    textAlign: `center`,
    width: `100%`,
    margin: `0 auto`,
    padding: `0 8px`,
  },
  title: {
    height: `24px`,
    marginLeft: `24px`,
    marginBottom: `4px`,
    fontWeight: `bold`,
    textAlign: `left`,
  },
  sup: {
    textAlign: `left`,
    fontSize: `0.6rem`,
    margin: `0 auto`,
    padding: `0 10px`,
  },
  text: {
    textAlign: `left`,
    fontSize: `1.0rem`,
    marginBottom: `4px`,
    padding: `0 4px`,
    borderBottom: `1px solid ${theme.palette.grey[500]}`,
  },
  right: {
    textAlign: `right`,
  },
  trendicon: {
    height: `24px`,
    width: `24px`,
    display: `inline`,
  },
  list: {
    //maxWidth: `1200px`,
    '&::-webkit-scrollbar': {
      width: `0.6em`,
      backgroundColor: theme.palette.scrollBarBackColor,
    },
    '&::-webkit-scrollbar-track': {
      boxShadow: `inset 0 0 6px transparent`,
      webkitBoxShadow: `inset 0 0 6px transparent`,
    },
    '&::-webkit-scrollbar-thumb': {
      height: `30px`,
      borderRadius: `3px`,
      boxShadow: `inset 0 0 30px 0 ${theme.palette.scrollBarForeColor}`,
      outline: `none`,
    },
    '& th': {
      backgroundClip: `padding-box`,
      borderRight: `1px solid transparent`,
    },
  },
  cellrank: {
    maxWidth: `55px`,
    textAlign: `right`,
  },
  cellname: {
    maxWidth: `400px`,
  },
  cellscore: {
    maxWidth: `120px`,
    textAlign: `right`,
  },
  cellrise: {
    maxWidth: `120px`,
    textAlign: `right`,
  },
  cellvar: {
    maxWidth: `100px`,
    textAlign: `right`,
  },
  cellcont: {
    maxWidth: `60px`,
    textAlign: `right`,
  },
  uptrend: {
    color: pink.A400,
    display: `flex`,
    justifyContent: `flex-end`,
  },
  downtrend: {
    color: green.A700,
    display: `flex`,
    justifyContent: `flex-end`,
  },
  link: {
    fontSize: `1.1em`,
    color: theme.palette.linkForeColor,
    '&:hover': {
      backgroundColor: theme.palette.linkHoverColor,
    },
  },
}))

export default function SenarioRank() {
  const {t} = useTranslation()
  const {dispatch} = useDispatch()
  const [cookies] = useCookies([])
  const {
    store: {
      locale,
      themeColor,
      perspective,
      tabs,
      termList,
      categoryList,
      mainType,
      mainGroup,
      mainCategory,
      mainList,
      mainTarget,
      mainTerm,
      mainBaseDate,
      categoryCodeList,
      senarioTermIds,
      senarioCategoryIds,
      senarioTrendIds,
      senarioDateList,
      senarioRank,
      rankingListTarget,
      rankingListParam,
    }
  } = useStore()
  const theme = useTheme(themeColor)
  const isMobile = useMediaQuery(theme.breakpoints.down(`xs`))
  const classes = useStyles({theme})
  const headers = isMobile ? [
    `rank`, //順位
    `targetEquity`, //`targetFx`
    `senarioScore`, //スコア
    ] : [
    `rank`, //順位
    `targetEquity`, //`targetFx`
    `senarioScore`, //スコア
    `senarioRise`, //騰落率
    `senarioVar`, //分散係数
    `senarioCont`, //連続係数
  ]
  const [ targetRank, setTargetRank ] = useState(null)
  const tableList = useRef(null)
  const [windowWidth, windowHeight] = useWindowSize()

  /** テーブルのヘッダ */
  const tableHead = useMemo(() => {
    return headers.map((head, i) => (
      <TableCell key={`snh${i}`} align="center">
        <div>
          {/* i18next-extract-disable-next-line */}
          <Typography>{nl2br(t(head))}</Typography>
        </div>
      </TableCell>
    ))
  }, [ locale ])

  /** シナリオ基準日リストを取得する処理 */
  const getBaseDate = () => {
    try {
      const group = mainGroup[perspective]
      const term = rankingListTarget.term
      return senarioDateList[group][term].split(` `)[0]
    } catch {
      return `Unknown date`
    }
  }

  /** シナリオランキングを取得する処理 */
  const loadSenarioRank = async (term, category, trend) => {
    const group = mainGroup[perspective]
    const baseDate = senarioDateList[group][term]
    const params = { group, term, baseDate }
    if (category !== null)
      params[`category`] = [ category ]

    //console.log(`loadSenarioRank started: ${group}, ${term}, ${category}, ${trend}, ${baseDate}`)

    rankingListParam[perspective] = {
      term: term,
      category: category,
      trend: trend,
      baseDate: baseDate,
    }
    dispatch({
      type: `RANKING_LIST_PARAM`,
      rankingListParam: rankingListParam
    })

    dispatch({
      type: `LOADING_MODAL`,
      loadingModal: {
        message: t(`loadingData`),
        state: true,
      },
    })

    let data = await getSenarioRankList(getTokenCookie(cookies), params)
    if (!Object.keys(data).length) throw `No data.`

    dispatch({
      type: `LOADING_MODAL`,
      loadingModal: {
        state: false,
      },
    })

    let ml = mainList[perspective]
    Object.entries(data).map(([k, list]) => {
      list.map((r, i) => {
        let m = ml.filter((v) => v.code === r.code)
        r.labelJa = m.length === 0 ? `undefined` : m[0].labelJa
        r.labelEn = m.length === 0 ? `undefined` : m[0].labelEn
        r.rank = i + 1
      })
    })

    senarioRank[perspective][term] = data
    dispatch({
      type: `SENARIO_RANK`,
      value: senarioRank,
    })

    setTargetRank([term, category, trend])

    //console.log(`loadSenarioRank done.`)
  }

  /** 指定した小数点以下桁数までで数値を返す
   * @param {number} v - 値
   * @param {number} r - 丸める小数点以下桁数
   * @param {bool} pad - 桁数揃えの有無
   */
  const getDV = useCallback((v, r=4, pad=true) => {
    let f = parseFloat(v)
    let d = 10 ** r
    let n = Math.round(f * d) / d
    if (pad) {
      let s = String(n).split(`.`)
      if (s.length == 1)
        return `${s[0]}.${`0`.repeat(r)}`
      else
        return `${s[0]}.${(s[1] + `0`.repeat(r)).substring(0, r)}`
    }
    return String(n)
  }, [])

  /** トレンドアイコンを返す
   * @param {number} v - 騰落率
   */
  const getTrendIcon = useCallback((v) => {
    if (v >= 0)
      return <TrendingUpIcon className={classes.trendicon} />
    else
      return <TrendingDownIcon className={classes.trendicon} />
  }, [classes])

  /** トレンドアイコン色を返す
   * @param {number} v - 騰落率
   */
  const getTrendColor = useCallback((v) => {
    return v >= 0 ? classes.uptrend : classes.downtrend
  }, [classes])

  // デバグ用ログ
  //useEffect(() => {
  //  console.log(`mainList: `, mainList)
  //}, [mainList])

  // メイングループが変更された場合の処理
  useEffect(() => {
    if (Object.keys(senarioDateList).length === 0)
      return
    if (rankingListTarget.term === null)
      return

    const baseDate = senarioDateList[mainGroup[perspective]][rankingListTarget.term]
    const wParams = rankingListParam[perspective]
    let wCategory = wParams.category
    if (!wCategory) {
      if (categoryList[perspective].find((h) => h.value === rankingListTarget.category)) {
        wCategory = rankingListTarget.category
      }
    }
    if (wParams.term === rankingListTarget.term && wParams.category === wCategory && wParams.baseDate === baseDate) {
      setTargetRank([rankingListTarget.term, wCategory, rankingListTarget.trend])
      return
    }

    loadSenarioRank(rankingListTarget.term, wCategory, rankingListTarget.trend)
  }, [perspective])

  // 表示条件が変更された場合の処理
  // Nakamura Memo : perspective 変更時に、時間差で表示条件が変わる場合がある（category が設定されている場合など）。
  //                 その場合にデータのロードやリストの作成などが２度行われてしまうため、perspective とは useEffect を分ける。
  useEffect(() => {
    if (Object.keys(senarioDateList).length === 0)
      return
    if (rankingListTarget.term === null)
      return

    const baseDate = senarioDateList[mainGroup[perspective]][rankingListTarget.term]
    const wParams = rankingListParam[perspective]
    if (wParams.term === rankingListTarget.term && wParams.category === rankingListTarget.category && wParams.baseDate === baseDate) {
      if (!targetRank || targetRank[0] !== rankingListTarget.term || targetRank[1] !== rankingListTarget.category || targetRank[2] !== rankingListTarget.trend) {
        setTargetRank([rankingListTarget.term, rankingListTarget.category, rankingListTarget.trend])
      }
      return
    }

    loadSenarioRank(rankingListTarget.term, rankingListTarget.category, rankingListTarget.trend)
  }, [rankingListTarget])

  // ランキング更新時に表のスクロールをトップに戻す
  useEffect(() => {
    tableList.current.scrollTop = 0
  }, [ targetRank ])

  // 銘柄クリック時の処理
  const handleTargetNameClick = async (e, r, i) => {
    const group = mainGroup[perspective]
    const term = rankingListTarget.term
    const category = rankingListTarget.category
    const baseDate = senarioDateList[group][term]
    const label = locale === `ja` ? `labelJa` : `labelEn`
    const emp = { key: null, label: null, labelJa: ``, labelEn: `` }

    // 銘柄データ
    const m = mainList[perspective].filter((v) => v.code === r.code)[0]

    // Perspective
    dispatch({
      type: `SET_MAIN_TARGET`,
      mainTarget: { ...mainTarget, [perspective]: {
        key: m.code,
        label: m[label],
        labelJa: m.labelJa,
        labelEn: m.labelEn,
      }},
    })

    // Term
    const te = termList[perspective].filter((v) => v.key === term)[0]
    dispatch({
      type: `SET_MAIN_TERM`,
      mainTerm: { ...mainTerm, [perspective]: {...te} },
    })

    // Category
    let cv = [ { ...emp } ]
    if (category) {
      const c = categoryList[perspective].filter((v) => v.value === category)[0]
      cv = { ...c }
    }
    dispatch({
      type: `SET_CATEGORY_CODE`,
      categoryCodeList: { ...categoryCodeList, [perspective]: cv },
    })

    const fetch = async () => {
      await loadBaseDate(group, term, r.code, cookies, dispatch)

      //基準日
      let b = {
        key: baseDate.split(` `)[0],
        label: baseDate.split(` `)[0],
      }
      dispatch({
        type: `SET_MAIN_BASE_DATE`,
        mainBaseDate: { ...mainBaseDate, [perspective]: b },
      })
      
      dispatch({
        type: `GET_SIMILAR`,
        getSimilar: true,
      })
    }
    fetch()

    // 類似チャートのページを表示
    dispatch({
      type: `PAGE`,
      page: `SIMILAR_CHART`,
    })
  }

  // シナリオランキング表本体
  const tableBody = useMemo(() => {
    if (targetRank == null)
      return null

    const label = locale === `ja` ? `labelJa` : `labelEn`
    const [ term, category, trend ] = targetRank

    const getRank = () => {
      try {
        return senarioRank[perspective][term][trend]
      } catch {
        return null
      }
    }
    const rank = getRank()
    if (!rank)
      return

    //console.log(`tableBody: `, perspective, term, category, trend, rank.length)
    
    return rank.map((r, i) => {
      return (
        <TableRow key={`srnk${i}`}>
          <TableCell className={classes.cellrank}>
            <Typography>{r.rank}</Typography>
          </TableCell>
          <TableCell className={`senarioBrand ` + classes.cellname}>
            <Typography className={classes.link} onClick={(e) => handleTargetNameClick(e, r, i)}>
              {r[label]}
            </Typography>
          </TableCell>
          <TableCell className={classes.cellscore}>
            <Typography>{getDV(Math.abs(r.indexValue), 4)}</Typography>
          </TableCell>
          {!isMobile && (
            <TableCell className={classes.cellrise}>
              <Typography className={getTrendColor(r.riseFall)}>
                {getTrendIcon(r.riseFall)}
                {getDV(r.riseFall * 100, 2)}%
              </Typography>
            </TableCell>
          )}
          {!isMobile && (
            <TableCell className={classes.cellvar}>
              <Typography>{getDV(r.variance)}</Typography>
            </TableCell>
          )}
          {!isMobile && (
            <TableCell className={classes.cellcont}>
              <Typography>{getDV(r.continuous, 4, false)}</Typography>
            </TableCell>
          )}
        </TableRow>
      )
    })
  }, [targetRank, locale, themeColor])

  // コンテンツ領域
  const content = useMemo(() => {
    const tabId = perspective ?? Object.keys(tabs)[0]
    const typ = mainType[perspective ?? 0]
    const compVal = typ === `FX` || typ === `FXSET` ? t(`middle`) : t(`close`)

    // リストの最大の高さ（クライアント領域の高さによって可変）
    let listMaxHeight = windowHeight
    const header = document.getElementById(`similarchart_header`)
    if (header)
      listMaxHeight -= header.offsetHeight  // ヘッダの高さ
    const footer = document.getElementById(`similarchart_footer`)
    if (footer)
      listMaxHeight -= footer.offsetHeight  // フッタの高さ
    else
      listMaxHeight -= 20                   // フッタが無い場合の微調整（下余白が少なすぎて見づらいため）
    listMaxHeight -= 28                     // title の高さ＋margin（style を変更したら修正が必要）
    listMaxHeight -= 24                     // root の margin（style を変更したら修正が必要）

    return (
      <div className={classes.grid}>
        <div>
          <div>
            <div className={classes.sup}>{t(`baseDate`)}</div>
            <div className={classes.text}>{getBaseDate()}</div>
          </div>
          <div>
            <div className={classes.sup}>{t(`senarioPeriod`)}</div>
            <div className={classes.text}>20 {t(`businessDay`)}</div>
          </div>
          <div>
            <div className={classes.sup}>{t(`comparisonValue`)}</div>
            <div className={classes.text}>{compVal}</div>
          </div>
          <SenarioTerm id={senarioTermIds[tabId]} />
          {(mainCategory[tabId] !== `none`) && (<SenarioCategory id={senarioCategoryIds[tabId]} />) }
          <SenarioTrend id={senarioTrendIds[tabId]} />
        </div>
        <div className={classes.content}>
          <TableContainer className={classes.list} ref={tableList} style={{maxHeight: listMaxHeight} }>
            <Table stickyHeader aria-label="enhanced table" size="small">
              <TableHead className={classes.thead}>
                <TableRow className={`senarioTable `}>{tableHead}</TableRow>
              </TableHead>
              <TableBody className={classes.tbody}>
                {tableBody}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      </div>
    )
  }, [ targetRank, locale, themeColor, windowHeight ])

  // ページ領域
  return (
    <div className={classes.root}>
      <div>
        <Typography className={classes.title}>
          {t(`titleSenario`)}
        </Typography>
      </div>
      {content}
    </div>
  )
}
